/**
 * TanStack Query bindings for the peer roster and its nested interfaces
 * (ADR-039 §D-1; feature-spec §2.11).
 *
 * The peer level of the surface :file:`use-peer-interfaces.ts` covers one level
 * down. Split into its own module rather than appended there because the two
 * answer different questions and have different cache lifetimes: the flat
 * interface list is deployment-wide chrome data the System display holds open,
 * while everything here is **configuration** that is not fetched at all until an
 * operator opens the dialog (REQ-UI-147).
 *
 * # Why the interface mutations live here and not beside their read
 *
 * Every interface mutation nests under its peer now (feature-spec §2.11), so it
 * needs a ``peerId`` its flat read never carried. Keeping the nested mutations
 * next to the nested read is what stops a caller reaching for the flat list's
 * hook and finding a mutation that silently wants a parent it does not have.
 *
 * # Cache invalidation
 *
 * A mutation at any level invalidates **both** the peer-scoped interface list
 * and the flat one. The flat list feeds the System display's icon stack, which
 * is a different surface entirely — an operator who deletes an interface here
 * and then closes the dialog must not find it still drawn on the diagram.
 *
 * Cross-references:
 *   - ADR-039 §D-1 (the peer entity); feature-spec §2.11 (route shape).
 *   - REQ-PEER-055 / -057 / -063 / -067 / -071 / -078.
 *   - REQ-UI-139 / -140 / -142 / -147.
 */
import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { ApiError, apiFetch } from "@/lib/api-client";
import type { components } from "@/lib/api-types.gen";
import { useSession } from "@/auth/session-context";
import {
  parsePeerInterfaceList,
  parsePeerList,
  type PeerConfig,
  type PeerInterfaceConfig,
  type PeerInterfaceView,
  type PeerView,
} from "@/peers/peer-model";
import type { InterfaceState } from "@/peers/peer-catalog";
import { PEER_INTERFACES_KEY, PEER_READER_ROLES } from "@/peers/use-peer-interfaces";

const BASE = "/api/admin/peers";

/** TanStack Query key for the peer roster. */
export const PEERS_KEY: readonly unknown[] = ["peer-manager", "peers"];

/** Query key for one peer's interfaces — invalidated by every mutation beneath it. */
export function peerInterfacesKey(peerId: string): readonly unknown[] {
  return ["peer-manager", "peers", peerId, "interfaces"];
}

function peerPath(peerId: string): string {
  return `${BASE}/${encodeURIComponent(peerId)}`;
}

function interfacePath(peerId: string, interfaceId: string): string {
  return `${peerPath(peerId)}/interfaces/${encodeURIComponent(interfaceId)}`;
}

/**
 * Fetch the peer roster (``GET /api/admin/peers``).
 *
 * @param enabled Whether to issue the request at all. **Not a convenience** —
 *   REQ-UI-147 requires the roster not be fetched until the dialog opens, and a
 *   caller that hoists this hook above the dialog would silently put a request
 *   on every session for a surface most sessions never open. Callers pass the
 *   dialog's open state; the role gate is applied on top of it here.
 */
export function usePeers(enabled = true): UseQueryResult<PeerView[], ApiError> {
  const session = useSession();
  return useQuery<PeerView[], ApiError>({
    queryKey: PEERS_KEY,
    enabled: enabled && session.hasAnyRole(PEER_READER_ROLES),
    queryFn: async () => parsePeerList(await apiFetch<unknown>(BASE)),
  });
}

/**
 * Fetch one peer's interfaces (``GET /api/admin/peers/{peer_id}/interfaces``).
 *
 * This is the read that makes the three-level roster work for a **non-privileged**
 * session. Interface→peer parentage is privileged as a *field* (feature-spec
 * §2.10), so a field-limited reader can never group the flat list under its
 * peers; asking for one peer's interfaces supplies the same grouping from the
 * path, and no field crosses the RBAC split to do it.
 *
 * @param peerId The selected peer, or ``null`` when nothing is selected.
 */
export function usePeerInterfacesFor(
  peerId: string | null,
): UseQueryResult<PeerInterfaceView[], ApiError> {
  const session = useSession();
  return useQuery<PeerInterfaceView[], ApiError>({
    queryKey: peerInterfacesKey(peerId ?? ""),
    enabled: peerId !== null && session.hasAnyRole(PEER_READER_ROLES),
    queryFn: async () =>
      parsePeerInterfaceList(await apiFetch<unknown>(`${peerPath(peerId ?? "")}/interfaces`)),
  });
}

/** Invalidate every peer-surface query — the roster, this peer's list, and the flat one. */
function useInvalidatePeerSurface(): (peerId: string) => void {
  const queryClient = useQueryClient();
  return (peerId: string) => {
    void queryClient.invalidateQueries({ queryKey: PEERS_KEY });
    void queryClient.invalidateQueries({ queryKey: peerInterfacesKey(peerId) });
    // The System display's icon stack reads the deployment-wide list. An operator
    // who deletes an interface here and closes the dialog must not find it still
    // drawn on the diagram.
    void queryClient.invalidateQueries({ queryKey: PEER_INTERFACES_KEY });
  };
}

/** Variables for {@link useSavePeer}. */
export interface SavePeerVars {
  config: PeerConfig;
  /** Optional operator ticket / justification, carried onto the audit event. */
  changeReference?: string;
}

/**
 * Create or replace one peer (``PUT …/{peer_id}``).
 *
 * A whole-document replace on a caller-supplied UUID, so a double-submit is safe
 * on a control that provisions an egress destination. The body's ``interfaces``
 * is always empty — the server does not write a peer's interface set through
 * this route, so a rename cannot delete everything beneath it.
 */
export function useSavePeer(): UseMutationResult<void, ApiError, SavePeerVars> {
  const invalidate = useInvalidatePeerSurface();
  return useMutation<void, ApiError, SavePeerVars>({
    mutationFn: async ({ config, changeReference }) => {
      const body: components["schemas"]["PutPeerRequest"] = { peer: config };
      const trimmed = changeReference?.trim();
      if (trimmed) body.change_reference = trimmed;
      await apiFetch<unknown>(peerPath(config.peer_id), {
        method: "PUT",
        body: JSON.stringify(body),
      });
    },
    onSuccess: (_result, { config }) => invalidate(config.peer_id),
  });
}

/**
 * Delete one peer (``DELETE …/{peer_id}``).
 *
 * **Cascades** to the peer's interfaces and their connections (REQ-PEER-057);
 * the Peer Manager's next reconcile releases each lease and deletes each
 * durable. Materially larger than deleting one interface, which is why the
 * confirmation names what goes with it.
 */
export function useDeletePeer(): UseMutationResult<void, ApiError, string> {
  const invalidate = useInvalidatePeerSurface();
  return useMutation<void, ApiError, string>({
    mutationFn: async (peerId) => {
      await apiFetch<void>(peerPath(peerId), { method: "DELETE" });
    },
    onSuccess: (_result, peerId) => invalidate(peerId),
  });
}

/** Variables for a start/stop at any level. */
export interface SetStateVars {
  peerId: string;
  state: InterfaceState;
  /** Optional operator ticket / justification, carried onto the audit event. */
  changeReference?: string;
}

/**
 * Start or stop a peer (``POST …/{peer_id}/state``) — the top-level kill switch.
 *
 * Stopping a peer suppresses every interface beneath it **without** overwriting
 * their recorded states (REQ-PEER-064), so starting it again resumes exactly the
 * set that was running before. The desired state is stated absolutely; there is
 * no toggle endpoint.
 */
export function useSetPeerState(): UseMutationResult<void, ApiError, SetStateVars> {
  const invalidate = useInvalidatePeerSurface();
  return useMutation<void, ApiError, SetStateVars>({
    mutationFn: async ({ peerId, state, changeReference }) => {
      await apiFetch<unknown>(`${peerPath(peerId)}/state`, {
        method: "POST",
        body: JSON.stringify(stateBody(state, changeReference)),
      });
    },
    onSuccess: (_result, { peerId }) => invalidate(peerId),
  });
}

/** Variables for {@link useSaveNestedInterface}. */
export interface SaveNestedInterfaceVars {
  peerId: string;
  config: PeerInterfaceConfig;
}

/**
 * Create or replace one interface under its peer
 * (``PUT …/{peer_id}/interfaces/{interface_id}``).
 *
 * ``peer_id`` rides **both** the path and the body, and the BFF refuses a
 * disagreement with `peer_id_mismatch`. That is not redundancy for its own sake:
 * the envelope stays the generated ``PutInterfaceRequest``, so `tsc` still
 * checks the body against the committed `openapi.yaml`, and addressing an
 * interface through the wrong peer surfaces as an explicit 400 rather than as a
 * silently accepted move under a different kill switch.
 */
export function useSaveNestedInterface(): UseMutationResult<
  void,
  ApiError,
  SaveNestedInterfaceVars
> {
  const invalidate = useInvalidatePeerSurface();
  return useMutation<void, ApiError, SaveNestedInterfaceVars>({
    mutationFn: async ({ peerId, config }) => {
      const body: components["schemas"]["PutInterfaceRequest"] = {
        peer_id: peerId,
        interface: config,
      };
      await apiFetch<unknown>(interfacePath(peerId, config.interface_id), {
        method: "PUT",
        body: JSON.stringify(body),
      });
    },
    onSuccess: (_result, { peerId }) => invalidate(peerId),
  });
}

/** Variables addressing one interface under its peer. */
export interface NestedInterfaceVars {
  peerId: string;
  interfaceId: string;
}

/** Delete one interface (``DELETE …/{peer_id}/interfaces/{interface_id}``). Audited; not undoable. */
export function useDeleteNestedInterface(): UseMutationResult<void, ApiError, NestedInterfaceVars> {
  const invalidate = useInvalidatePeerSurface();
  return useMutation<void, ApiError, NestedInterfaceVars>({
    mutationFn: async ({ peerId, interfaceId }) => {
      await apiFetch<void>(interfacePath(peerId, interfaceId), { method: "DELETE" });
    },
    onSuccess: (_result, { peerId }) => invalidate(peerId),
  });
}

/** Variables for the interface start/stop mutation. */
export interface SetNestedInterfaceStateVars extends NestedInterfaceVars {
  state: InterfaceState;
  changeReference?: string;
}

/** Start or stop one interface (``POST …/{peer_id}/interfaces/{id}/state``). */
export function useSetNestedInterfaceState(): UseMutationResult<
  void,
  ApiError,
  SetNestedInterfaceStateVars
> {
  const invalidate = useInvalidatePeerSurface();
  return useMutation<void, ApiError, SetNestedInterfaceStateVars>({
    mutationFn: async ({ peerId, interfaceId, state, changeReference }) => {
      await apiFetch<unknown>(`${interfacePath(peerId, interfaceId)}/state`, {
        method: "POST",
        body: JSON.stringify(stateBody(state, changeReference)),
      });
    },
    onSuccess: (_result, { peerId }) => invalidate(peerId),
  });
}

/** Variables for the per-connection start/stop mutation. */
export interface SetNestedConnectionStateVars extends SetNestedInterfaceStateVars {
  connectionId: string;
}

/** Start or stop one connection (``POST …/{peer_id}/interfaces/{id}/connections/{id}/state``). */
export function useSetNestedConnectionState(): UseMutationResult<
  void,
  ApiError,
  SetNestedConnectionStateVars
> {
  const invalidate = useInvalidatePeerSurface();
  return useMutation<void, ApiError, SetNestedConnectionStateVars>({
    mutationFn: async ({ peerId, interfaceId, connectionId, state, changeReference }) => {
      const path = `${interfacePath(peerId, interfaceId)}/connections/${encodeURIComponent(connectionId)}/state`;
      await apiFetch<unknown>(path, {
        method: "POST",
        body: JSON.stringify(stateBody(state, changeReference)),
      });
    },
    onSuccess: (_result, { peerId }) => invalidate(peerId),
  });
}

/** Variables addressing one connection under its interface, under its peer. */
export interface NestedConnectionVars extends NestedInterfaceVars {
  connectionId: string;
}

/**
 * Delete one connection (``DELETE …/interfaces/{id}/connections/{id}``).
 *
 * **Distinct from stopping it** (REQ-PEER-105, REQ-UI-154). Stopping silences a
 * connection and keeps it, so starting the interface brings it back; this
 * removes it from the configuration and it does not. The two are separate
 * endpoints precisely so they land as separate audit events, and the UI words
 * them apart for the same reason — an operator reaching for a kill switch must
 * not decommission a path instead.
 *
 * Deleting the **last** connection is permitted: an interface with no
 * connections is a legitimate configured state (it is what `Add interface`
 * produces), so refusing would make the surface unable to return to a state it
 * can create. The interface then emits nothing, and says so.
 */
export function useDeleteNestedConnection(): UseMutationResult<
  void,
  ApiError,
  NestedConnectionVars
> {
  const invalidate = useInvalidatePeerSurface();
  return useMutation<void, ApiError, NestedConnectionVars>({
    mutationFn: async ({ peerId, interfaceId, connectionId }) => {
      const path = `${interfacePath(peerId, interfaceId)}/connections/${encodeURIComponent(connectionId)}`;
      await apiFetch<void>(path, { method: "DELETE" });
    },
    onSuccess: (_result, { peerId }) => invalidate(peerId),
  });
}

/**
 * The installer's verdict on a bundle, as ``POST …/peers/import`` reports it.
 *
 * The three outcomes are kept **apart**, not collapsed into a success flag.
 * Apply-once (REQ-PEER-102) means an unchanged re-import changes nothing and an
 * *edited* re-import is refused rather than applied — and refusing is the point,
 * because applying would resurrect interfaces an operator deliberately deleted.
 * A bare 200 makes both look like a successful install (REQ-UI-150).
 */
export interface PeerImportOutcome {
  applied: string[];
  already_applied: string[];
  /** ``(bundle, reason)`` pairs — the operator needs both to know whether to retry. */
  rejected: { bundle: string; reason: string }[];
  skipped: string[];
  /**
   * The subset of `applied` that overwrote a peer this deployment already had —
   * a deliberate re-import of an edited bundle. Kept apart so the dialog says
   * "replaced", not "installed", to an operator who just overwrote a live one.
   */
  replaced: string[];
  peer: { name: string } | null;
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
}

/**
 * Parse the import response.
 *
 * Hand-rolled for the reason the rest of this surface is (``peer-model``): the
 * route returns :class:`JSONResponse`, so its schema is ``{}`` in the committed
 * ``openapi.yaml`` and ``openapi-typescript`` emits ``unknown``. Missing keys
 * degrade to empty rather than throwing — unlike a *read* of live egress
 * configuration, where a half-parsed body would hide a running flow. Here the
 * write has already happened server-side and the worst case is an under-reported
 * summary, so refusing to render it would tell the operator less, not more.
 */
export function parsePeerImportOutcome(body: unknown): PeerImportOutcome {
  const record = (typeof body === "object" && body !== null ? body : {}) as Record<string, unknown>;
  const rejected = Array.isArray(record.rejected) ? record.rejected : [];
  const peer = record.peer;
  return {
    applied: asStringArray(record.applied),
    already_applied: asStringArray(record.already_applied),
    rejected: rejected.flatMap((entry) => {
      if (typeof entry !== "object" || entry === null) return [];
      const { bundle, reason } = entry as { bundle?: unknown; reason?: unknown };
      return typeof bundle === "string"
        ? [{ bundle, reason: typeof reason === "string" ? reason : "no reason given" }]
        : [];
    }),
    skipped: asStringArray(record.skipped),
    replaced: asStringArray(record.replaced),
    peer:
      typeof peer === "object" &&
      peer !== null &&
      typeof (peer as { name?: unknown }).name === "string"
        ? { name: (peer as { name: string }).name }
        : null,
  };
}

/**
 * Install a peer from a bundle on the operator's workstation
 * (``POST …/peers/import``) — REQ-UI-150 / REQ-PEER-103.
 *
 * The BFF delegates to the same staged-bundle installer a boot-time install
 * uses, so an interactive import takes the identical validate → store artifacts
 * → persist → audit path. This hook therefore does **not** audit, report success
 * on its own, or interpret the outcome — it returns the installer's verdict
 * verbatim for the dialog to state.
 */
export function useImportPeerBundle(): UseMutationResult<PeerImportOutcome, ApiError, File> {
  const queryClient = useQueryClient();
  return useMutation<PeerImportOutcome, ApiError, File>({
    mutationFn: async (file) => {
      const form = new FormData();
      form.append("file", file);
      // No Content-Type: the browser must set the multipart boundary.
      return parsePeerImportOutcome(
        await apiFetch<unknown>(`${BASE}/import`, { method: "POST", body: form }),
      );
    },
    // An import creates a peer this client has never seen, so there is no
    // peer id to scope the invalidation by — the roster and the flat list are
    // both stale and neither is addressed by `useInvalidatePeerSurface`.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PEERS_KEY });
      void queryClient.invalidateQueries({ queryKey: PEER_INTERFACES_KEY });
    },
  });
}

/** Variables for {@link useExportPeerBundle}. */
export interface ExportPeerVars {
  peerId: string;
  /** Used only for the download filename — the archive's contents come from the server. */
  peerName: string;
}

/**
 * Download one peer as an installable bundle (``GET …/{peer_id}/export``) —
 * REQ-UI-151 / REQ-PEER-104.
 *
 * A **projection, not a replay** (feature-spec D-6): every member is rendered
 * from the control-plane rows at the moment of the request, so a peer renamed in
 * this dialog exports under its new name.
 *
 * ⚠ ``meridian_key_ref`` never leaves — it names material this deployment holds
 * and cannot prove is public. The surface states that beside the control rather
 * than leaving the operator to notice the absence (REQ-UI-151).
 *
 * Read through ``rawResponse`` so this client's CSRF header, credential handling
 * and error mapping still apply to a route that answers with a file.
 */
export function useExportPeerBundle(): UseMutationResult<void, ApiError, ExportPeerVars> {
  return useMutation<void, ApiError, ExportPeerVars>({
    mutationFn: async ({ peerId, peerName }) => {
      const response = await apiFetch<Response>(`${peerPath(peerId)}/export`, {
        rawResponse: true,
      });
      downloadBlob(await response.blob(), `${peerName}-bundle.tar.gz`);
    },
  });
}

/**
 * Hand a fetched archive to the browser's own download handling.
 *
 * No-ops outside a browser so the call is safe in a test context that has not
 * stubbed the DOM — the mutation still resolves, which is what the caller
 * asserts on.
 */
function downloadBlob(blob: Blob, filename: string): void {
  if (typeof document === "undefined" || typeof URL.createObjectURL !== "function") return;
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** ``change_reference`` is omitted rather than sent empty, so the audit record carries no noise. */
function stateBody(
  state: InterfaceState,
  changeReference: string | undefined,
): Record<string, unknown> {
  const trimmed = changeReference?.trim();
  return trimmed ? { state, change_reference: trimmed } : { state };
}
